import { useEffect, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useParams } from "react-router-dom"
import { Calendar, Clock, Globe, Users, CheckCircle2, Mail, User, Phone, Building2, ArrowRight, Video } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { ButtonLoader, FullPageDotsLoader } from "@/clientWeb/components/ui/page-loader"
import { webinarPublicService, type WebinarPublicSummary } from "./services/webinar-public.service"

export default function WebinarRegistrationPage() {
  const t = useTranslation()
  const { id } = useParams()
  const [webinar, setWebinar] = useState<WebinarPublicSummary | null>(null)
  const [loadingWebinar, setLoadingWebinar] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoadingWebinar(true)
    webinarPublicService.getPublicSummary(id ?? 'w1')
      .then((data) => { if (!cancelled) setWebinar(data) })
      .catch((err) => {
        if (cancelled) return
        toast.error({ title: 'Failed to load webinar', description: err instanceof Error ? err.message : 'Please try again' })
      })
      .finally(() => { if (!cancelled) setLoadingWebinar(false) })
    return () => { cancelled = true }
  }, [id])

  const [step, setStep] = useState<"form" | "success">("form")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", company: "", role: "" })
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({})

  const handleChange = (k: keyof typeof form, maxLen: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [k]: e.target.value.slice(0, maxLen) }))
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: '' }))
  }

  const validate = () => {
    const next: Partial<Record<keyof typeof form, string>> = {}
    if (!form.firstName.trim()) next.firstName = t("webinar.firstName") + ' is required'
    if (!form.lastName.trim()) next.lastName = t("webinar.lastName") + ' is required'
    if (!form.email.trim()) next.email = t("webinar.enterValidEmail")
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = t("webinar.invalidEmail")
    if (form.phone.trim() && !/^[+]?[\d\s\-().]{7,20}$/.test(form.phone.trim())) next.phone = 'Invalid phone number'
    return next
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const next = validate()
    if (Object.keys(next).length) {
      setErrors(next)
      return
    }
    setIsSubmitting(true)
    await new Promise(r => setTimeout(r, 1200))
    setIsSubmitting(false)
    setStep("success")
    toast.success({ title: t("webinar.registrationConfirmed"), description: t("webinar.checkEmailForLink") })
  }

  if (loadingWebinar || !webinar) {
    return <FullPageDotsLoader />
  }

  const spotsLeft = webinar.capacity - webinar.registeredCount
  const pct = Math.round((webinar.registeredCount / webinar.capacity) * 100)

  if (step === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-violet-50/30 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-3xl shadow-2xl p-8 flex flex-col items-center gap-5 text-center animate-in zoom-in duration-500">
          <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">{t("webinar.youreRegistered")}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              {t("webinar.confirmationSentTo")} <strong>{form.email}</strong>.
            </p>
          </div>
          <div className="w-full bg-muted/50 rounded-2xl p-4 text-sm space-y-1.5">
            <p className="font-medium text-foreground truncate">{webinar.title}</p>
            <p className="text-muted-foreground flex items-center gap-1.5 justify-center"><Calendar className="w-3.5 h-3.5" />{webinar.date}</p>
            <p className="text-muted-foreground flex items-center gap-1.5 justify-center"><Clock className="w-3.5 h-3.5" />{webinar.time}</p>
          </div>
          <p className="text-xs text-muted-foreground">{t("webinar.addToCalendar")} · {t("webinar.shareWithColleagues")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-violet-50/30 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Hero Banner */}
      <div className="relative h-52 sm:h-64 overflow-hidden">
        <img src={webinar.banner} alt={webinar.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/70" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold">{t("webinar.liveWebinar").toUpperCase()}</span>
              <span className="px-2.5 py-1 rounded-full bg-card/20 text-white text-xs font-medium backdrop-blur-sm">
                {spotsLeft} {t("webinar.spotsLeft")}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white leading-snug">{webinar.title}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-5 gap-8">
        {/* Left — Info */}
        <div className="lg:col-span-3 space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Calendar, label: webinar.date },
              { icon: Clock,    label: webinar.time },
              { icon: Globe,    label: webinar.timezone },
              { icon: Users,    label: `${webinar.registeredCount} ${t("webinar.registered").toLowerCase()}` },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 bg-card border border-border rounded-2xl px-4 py-3">
                <Icon className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm text-foreground font-medium leading-tight">{label}</span>
              </div>
            ))}
          </div>

          {/* Capacity bar */}
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground">{t("webinar.registrationProgress")}</span>
              <span className="text-xs text-muted-foreground">{webinar.registeredCount}/{webinar.capacity}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">{spotsLeft} {t("webinar.spotsRemaining")}</p>
          </div>

          {/* Description */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">{t("webinar.aboutThisWebinar")}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{webinar.description}</p>
          </div>

          {/* Topics */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">{t("webinar.whatYoullLearn")}</h3>
            <div className="space-y-2">
              {webinar.topics.map(t => (
                <div key={t} className="flex items-center gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  {t}
                </div>
              ))}
            </div>
          </div>

          {/* Speakers */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">{t("webinar.speakers")}</h3>
            <div className="flex flex-wrap gap-3">
              {webinar.speakers.map(s => (
                <div key={s.name} className="flex items-center gap-3 bg-card border border-border rounded-2xl px-4 py-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 bg-muted">
                    <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Registration Form */}
        <div className="lg:col-span-2">
          <div className="sticky top-6 bg-card border border-border rounded-3xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-primary/80 px-5 py-4">
              <div className="flex items-center gap-2 mb-1">
                <Video className="w-4 h-4 text-white" />
                <span className="text-white text-xs font-semibold uppercase tracking-wide">{t("webinar.freeRegistration")}</span>
              </div>
              <p className="text-white text-sm opacity-90">{t("webinar.reserveYourSpot")}</p>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("webinar.firstName")} *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input value={form.firstName} onChange={handleChange("firstName", 50)} placeholder="John" maxLength={50} className={`pl-9 h-9 text-sm ${errors.firstName ? 'border-destructive focus-visible:ring-destructive' : ''}`} />
                  </div>
                  {errors.firstName && <p className="text-[11px] text-destructive mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("webinar.lastName")} *</label>
                  <Input value={form.lastName} onChange={handleChange("lastName", 50)} placeholder="Doe" maxLength={50} className={`h-9 text-sm ${errors.lastName ? 'border-destructive focus-visible:ring-destructive' : ''}`} />
                  {errors.lastName && <p className="text-[11px] text-destructive mt-1">{errors.lastName}</p>}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("webinar.emailAddress")} *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input type="email" value={form.email} onChange={handleChange("email", 254)} placeholder="john@company.com" maxLength={254} className={`pl-9 h-9 text-sm ${errors.email ? 'border-destructive focus-visible:ring-destructive' : ''}`} />
                </div>
                {errors.email && <p className="text-[11px] text-destructive mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("webinar.phone")}</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input value={form.phone} onChange={handleChange("phone", 20)} placeholder="+1 234 567 8900" maxLength={20} className={`pl-9 h-9 text-sm ${errors.phone ? 'border-destructive focus-visible:ring-destructive' : ''}`} />
                </div>
                {errors.phone && <p className="text-[11px] text-destructive mt-1">{errors.phone}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("webinar.company")}</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input value={form.company} onChange={handleChange("company", 100)} placeholder="Acme Inc." maxLength={100} className="pl-9 h-9 text-sm" />
                </div>
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full h-10 mt-2 gap-2">
                {isSubmitting ? <ButtonLoader /> : <><ArrowRight className="w-4 h-4" />{t("webinar.registerNow")}</>}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                {t("webinar.byRegisteringAgree")}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
